import type { DateTime } from "luxon";
import { isNurseActiveInDepartment } from "@/utils/formatters";
import type { Nurse, NurseFullRole } from "@/utils/models";

/**
 * Everything that decides whether a nurse is offered for a shift by an "add someone" picker. Every key
 * is required so each surface states its choices; a check is switched off only by its explicit value
 * (`null` / `undefined` / an empty set / `false`), as documented per field.
 *
 * PTO is deliberately NOT a criterion: a nurse on PTO is still offered, and the add flow warns when she
 * is picked.
 */
export type AddNurseCandidateCriteria = {
	/** Roles the picker is showing. */
	roles: readonly NurseFullRole[];
	/** Employment types the picker is showing (`EmploymentType` values). */
	contractTypes: readonly string[];
	/** Department the nurse must be active in; `null` skips the check (the desktop actual view never applied it). */
	activeInDepartmentId: string | null;
	/** Drop nurses whose scheduled deactivation falls before this day; `null` skips the check. */
	deactivationCutoff: DateTime | null;
	/** Only nurses who can take charge — relief charge or shift manager. */
	chargeOnly: boolean;
	/** Nurses never offered (e.g. the ones a request already covers); `undefined` means none. */
	nurseIdsToIgnore: readonly number[] | undefined;
	/** A request's job: only nurses holding it; `undefined` means any job. */
	requiredJobId: string | undefined;
	/** Nurses already working this shift, left out so no one is offered twice. */
	nurseIdsOnShift: ReadonlySet<number>;
};

/** Whether `nurse` is offered as a candidate under `criteria`. Pure; the order of the list is the caller's. */
export const isAddNurseCandidate = (nurse: Nurse, criteria: AddNurseCandidateCriteria): boolean => {
	if (!criteria.roles.includes(nurse.role) || !criteria.contractTypes.includes(nurse.employmentType)) return false;
	if (criteria.nurseIdsOnShift.has(nurse.id)) return false;
	if (criteria.activeInDepartmentId !== null && !isNurseActiveInDepartment(nurse, criteria.activeInDepartmentId)) return false;
	if (criteria.deactivationCutoff) {
		const activeThroughCutoff = !nurse.scheduledDeactivationDate || nurse.scheduledDeactivationDate >= criteria.deactivationCutoff.startOf("day");
		if (!activeThroughCutoff) return false;
	}
	if (criteria.chargeOnly && !(nurse.canBeReliefCharge || nurse.isShiftManager)) return false;
	if (criteria.nurseIdsToIgnore?.includes(nurse.id)) return false;
	if (criteria.requiredJobId && !nurse.jobIds?.includes(criteria.requiredJobId)) return false;
	return true;
};
